import { useEffect, useRef } from "react";

// Public site key (safe to ship in the browser). REACT_APP_TURNSTILE_SITE_KEY overrides it, e.g. for staging.
const SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAAFYh1wPwy4gMZ1QR";

// Must match Turnstile:Action in the Orders API.
const ACTION = "place-order";

/**
 * Cloudflare Turnstile widget. Calls onToken(token) whenever a new token is issued (tokens auto-refresh before they expire)
 * and onToken("") when the token expires or the challenge fails. Calls onUnavailable() if the Turnstile script has not
 * loaded after 10 seconds. Change resetKey to request a fresh token, e.g. after a failed order (tokens are single-use).
 */
export default function Turnstile({ onToken, onUnavailable, resetKey }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const render = () => {
      if (cancelled || !containerRef.current) return;
      if (!window.turnstile) {
        if (attempts++ < 100) setTimeout(render, 100);
        else if (onUnavailableRef.current) onUnavailableRef.current();
        return;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        action: ACTION,
        "refresh-expired": "auto",
        callback: (token) => onTokenRef.current(token),
        "expired-callback": () => onTokenRef.current(""),
        // A widget error (e.g. hostname not allowed) must not block card checkout; the Orders API decides.
        "error-callback": () => {
          onTokenRef.current("");
          if (onUnavailableRef.current) onUnavailableRef.current();
        },
      });
    };
    render();

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (resetKey && window.turnstile && widgetIdRef.current) {
      onTokenRef.current("");
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [resetKey]);

  return <div ref={containerRef} className="flex justify-center my-4" />;
}
